import {Link, useLocation} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileLines, faFolder, faFolderOpen} from "@fortawesome/free-solid-svg-icons";
import {normalizePath, siteTree} from "./siteTree";
import "../../styles/FileTree.css";

const containsPath = (node, pathname) =>
    node.to === pathname || (node.children || []).some((child) => containsPath(child, pathname));

// Ids of every folder on the way to the current page: the only ones shown expanded
function foldersAlongPath(node, pathname, found = []) {
    if (node.children && containsPath(node, pathname)) {
        found.push(node.id);
        node.children.forEach((child) => foldersAlongPath(child, pathname, found));
    }
    return found;
}

function Row({node, depth, pathname, openFolders}) {
    const isFolder = Boolean(node.children);
    // the root stays open so unlisted routes (e.g. /tech-stack) still show the tree
    const isOpen = isFolder && (depth === 0 || openFolders.has(node.id));
    const isActive = node.to === pathname;
    const icon = isFolder ? (isOpen ? faFolderOpen : faFolder) : faFileLines;
    const label = (
        <>
            <FontAwesomeIcon icon={icon} className={isFolder ? "ft-icon ft-folder" : "ft-icon"} fixedWidth/>
            <span className="ft-label">{node.name}</span>
        </>
    );

    return (
        <li role="treeitem" aria-expanded={isFolder ? isOpen : undefined}>
            <div className={isActive ? "ft-row ft-active" : "ft-row"} style={{"--depth": depth}}>
                {node.to ? (
                    <Link to={node.to} className="ft-link" aria-current={isActive ? "page" : undefined}>
                        {label}
                    </Link>
                ) : (
                    <span className="ft-link ft-disabled" aria-disabled="true">
                        {label}
                    </span>
                )}
            </div>
            {isOpen && (
                <ul role="group">
                    {node.children.map((child) => (
                        <Row
                            key={child.id || child.name}
                            node={child}
                            depth={depth + 1}
                            pathname={pathname}
                            openFolders={openFolders}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}

export default function FileTree() {
    const pathname = normalizePath(useLocation().pathname);
    const openFolders = new Set(foldersAlongPath(siteTree, pathname));

    return (
        <nav className="file-tree" aria-label="Site navigation">
            <ul role="tree">
                <Row node={siteTree} depth={0} pathname={pathname} openFolders={openFolders}/>
            </ul>
        </nav>
    );
}
